'use client';

import React, { useState } from 'react';
import { Clock, Award, ChevronRight, Flame, Film } from 'lucide-react';
import ClipCard from '@/components/ClipCard';
import CategoryBar from '@/app/components/CategoryBar';
import TrendingBanner from '@/app/components/TrendingBanner';
import SectionHeader from '@/app/components/SectionHeader';
import { useClips } from '@/lib/useClips';

function EmptyClipsState({ message }: { message: string }) {
  return (
    <div
      className="w-full rounded-2xl flex flex-col items-center justify-center py-16 px-6 text-center"
      style={{ background: 'var(--muted)', border: '1px dashed var(--border)' }}
    >
      <Film size={40} style={{ color: 'var(--muted-foreground)', opacity: 0.4 }} className="mb-3" />
      <p className="text-sm font-medium" style={{ color: 'var(--muted-foreground)' }}>
        {message}
      </p>
    </div>
  );
}

export default function HomeContent() {
  const [activeCategory, setActiveCategory] = useState('all');

  const { clips: trendingClips, loading: loadingTrending, isEmpty: emptyTrending } = useClips({ sort: 'trending', limit: 8 });
  const { clips: newClips, loading: loadingNew, isEmpty: emptyNew } = useClips({ sort: 'newest', limit: 6 });
  const { clips: editorsPicksClips, loading: loadingPicks, isEmpty: emptyPicks } = useClips({ sort: 'editors-picks', limit: 4 });

  return (
    <div className="max-w-screen-2xl mx-auto px-4 xl:px-8 2xl:px-12">
      {/* Category Bar */}
      <div className="pt-4 pb-2">
        <CategoryBar active={activeCategory} onSelect={setActiveCategory} />
      </div>

      {/* Trending Banner */}
      <div className="mt-4">
        <TrendingBanner />
      </div>

      {/* Trending Section */}
      <section className="mt-8">
        <SectionHeader
          icon={<Flame size={18} style={{ color: '#FF6B35' }} />}
          title="Trending Now"
          subtitle="Most downloaded in the last 24 hours"
          accentColor="#FF6B35"
        />
        <div className="mt-4">
          {loadingTrending ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="rounded-2xl animate-pulse" style={{ background: 'var(--muted)', height: 220 }} />
              ))}
            </div>
          ) : emptyTrending ? (
            <EmptyClipsState message="No trending clips yet — be the first creator to upload a clip!" />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-4 gap-4">
              {trendingClips.map((clip) => <ClipCard key={clip.id} clip={clip} />)}
            </div>
          )}
        </div>
        {!emptyTrending && (
          <button className="btn-ghost w-full mt-4 py-3 text-sm flex items-center justify-center gap-2">
            View all trending clips <ChevronRight size={15} />
          </button>
        )}
      </section>

      {/* New Uploads Section */}
      <section className="mt-10">
        <SectionHeader
          icon={<Clock size={18} style={{ color: 'var(--accent)' }} />}
          title="Newly Uploaded"
          subtitle="Fresh clips from the community"
          accentColor="var(--accent)"
        />
        <div className="mt-4">
          {loadingNew ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="rounded-2xl animate-pulse" style={{ background: 'var(--muted)', height: 220 }} />
              ))}
            </div>
          ) : emptyNew ? (
            <EmptyClipsState message="No clips uploaded yet — be the first to share your work!" />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6 gap-4">
              {newClips.map((clip) => <ClipCard key={clip.id} clip={clip} />)}
            </div>
          )}
        </div>
        {!emptyNew && (
          <button className="btn-ghost w-full mt-4 py-3 text-sm flex items-center justify-center gap-2">
            View all new clips <ChevronRight size={15} />
          </button>
        )}
      </section>

      {/* Editor's Picks */}
      <section className="mt-10 mb-8">
        <SectionHeader
          icon={<Award size={18} style={{ color: 'var(--premium)' }} />}
          title="Editor's Picks"
          subtitle="Curated by the ClipHub team"
          accentColor="var(--premium)"
          badge="Curated"
        />
        <div className="mt-4">
          {loadingPicks ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="rounded-2xl animate-pulse" style={{ background: 'var(--muted)', height: 260 }} />
              ))}
            </div>
          ) : emptyPicks ? (
            <EmptyClipsState message="Editor's picks coming soon — check back after creators start uploading!" />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-4 gap-4">
              {editorsPicksClips.map((clip) => <ClipCard key={clip.id} clip={clip} variant="featured" />)}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}